/**
 * Prompt Generator Utility
 * Creates structured prompts for AI APIs
 */

/**
 * Generate a prompt for meal plan generation
 * @param {Array} foods - Array of available foods
 * @param {Object} goal - User goal (bulk, cut, maintain)
 * @param {number} calories - Target calories per day
 * @returns {string} Formatted prompt for AI
 */
export function generateMealPlanPrompt(foods, goal = 'maintain', calories = 2000) {
  const foodNames = foods.map(f => f.name).join(', ');
  
  return `You are a nutrition expert. Generate a personalized 7-day meal plan.

USER DATA:
Available Foods: ${foodNames}
Goal: ${goal === 'bulk' ? 'Muscle Building' : goal === 'cut' ? 'Fat Loss' : 'Maintenance'}
Target Calories: ${calories} kcal per day
Target Protein: ${Math.round(calories * 0.3 / 4)}g per day (30% of calories)

REQUIREMENTS:
1. Use ONLY the foods provided
2. Plan 3 meals per day (Breakfast, Lunch, Dinner)
3. Include snacks if needed to meet calorie goals
4. Ensure adequate protein in every meal
5. Make it realistic and sustainable
6. Vary meals across the week

OUTPUT FORMAT (JSON):
{
  "goal": "${goal}",
  "caloriesPerDay": ${calories},
  "days": [
    {
      "day": 1,
      "breakfast": {
        "meal": "Meal description",
        less"calories": 400,
        "protein": 30,
        "carbs": 50,
        "fat": 10
      },
      "lunch": { ... },
      "dinner": { ... },
      "snack": { ... }
    }
  ]
}

Generate the complete 7-day plan now:`;
}

/**
 * Generate a prompt for workout generation
 * @param {string} goal - Fitness goal
 * @param {number} experience - User experience level (1-3)
 * @returns {string} Formatted workout prompt
 */
export function generateWorkoutPrompt(goal = 'maintain', experience = 2) {
  const level = experience === 1 ? 'Beginner' : experience === 3 ? 'Advanced' : 'Intermediate';
  
  return `Generate 7 daily workouts for a ${level} fitness enthusiast with goal: ${goal === 'bulk' ? 'Muscle Building' : goal === 'cut' ? 'Fat Loss' : 'General Fitness'}.

Each day should focus on:
Day 1: Push (Chest, Shoulders, Triceps)
Day 2: Pull (Back, Biceps)
Day 3: Legs (Quads, Hamstrings, Glutes)
Day 4: Cardio or HIIT
Day 5: Full Body
Day 6: Core + Mobility
Day 7: Active Recovery / Stretching

OUTPUT FORMAT (JSON):
{
  "workouts": [
    {
      "day": 1,
      "focus": "Push Day",
      "duration": "45-60 min",
      "exercises": [
        {"name": "Exercise name", "sets": 3, "reps": "10-12", "rest": "60s"}
      ]
    }
  ]
}

Generate workouts:`;
}

/**
 * Generate summarization prompt
 * @param {string} content - Content to summarize
 * @returns {string} Summarization prompt
 */
export function generateSummarizePrompt(content) {
  return `Summarize the following fitness plan in 2-3 bullet points, highlighting the key benefits:
  
${content.substring(0, 500)}...
  
Provide a concise summary:`;
}


